/**
 * Internals shared by Tabs (src/lib/ui/Tabs.tsx), SegmentedControl (its
 * sliding pill) and the deprecated SubheaderTab* wrappers
 * (src/lib/ui/SubheaderTabStrip.tsx): the sliding
 * hover/selection pill, the 1px underline on the row divider, and the
 * horizontal scroller with edge fades and page buttons. Not a public
 * primitive: build tab rows with Tabs.
 */
import {
  CSSProperties,
  FC,
  ForwardedRef,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import ChevronLeftIcon from "@src/lib/icons/12px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import { cn } from "@src/lib/utils/cn";

type TabEntry = {
  node: HTMLElement;
  active: boolean;
};

export type TabStripIndicatorRect = {
  left: number;
  width: number;
  visible: boolean;
};

const HIDDEN_RECT: TabStripIndicatorRect = {
  left: 0,
  width: 0,
  visible: false,
};

/** The underline is inset 8px on each side of the active tab. */
export const TAB_STRIP_UNDERLINE_INSET = 8;

export type TabStripContextValue = {
  registerTab: (id: string, node: HTMLElement | null, active: boolean) => void;
  setHoveredTabId: (id: string | null) => void;
};

export const TabStripContext = createContext<TabStripContextValue | null>(null);

/**
 * A tab's left edge (in the list's scrolled content) and width. Layout
 * offsets when the tab sits directly in the list: they ignore ancestor
 * transforms, so a dialog's zoom-in doesn't shrink the indicators. Rects
 * otherwise (wrapped tabs, and jsdom, which has no offsetParent).
 */
export function tabBox(
  node: HTMLElement,
  list: HTMLElement,
  listRect: DOMRect,
): { left: number; width: number } {
  if (node.offsetParent === list) {
    return { left: node.offsetLeft, width: node.offsetWidth };
  }
  const rect = node.getBoundingClientRect();
  return {
    left: Math.round(rect.left - listRect.left + list.scrollLeft),
    width: Math.round(rect.width),
  };
}

const sameRect = (a: TabStripIndicatorRect, b: TabStripIndicatorRect) =>
  a.left === b.left && a.width === b.width && a.visible === b.visible;

/**
 * Measures the active (or hovered) tab inside a list and returns where the
 * pill and the underline should sit. Tabs register through
 * `useTabStripRegistration` under `TabStripContext`.
 */
export type TabStripIndicatorsState<T extends HTMLElement> = {
  setListRef: (node: T | null) => void;
  pill: TabStripIndicatorRect;
  underline: TabStripIndicatorRect;
} & TabStripContextValue;

export function useTabStripIndicators<T extends HTMLElement>(
  forwardedRef?: ForwardedRef<T>,
): TabStripIndicatorsState<T> {
  const listRef = useRef<T | null>(null);
  const tabsRef = useRef(new Map<string, TabEntry>());
  const hoveredTabIdRef = useRef<string | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const measureRef = useRef<() => void>(() => {});
  const [pill, setPill] = useState<TabStripIndicatorRect>(HIDDEN_RECT);
  const [underline, setUnderline] =
    useState<TabStripIndicatorRect>(HIDDEN_RECT);

  const setListRef = useCallback(
    (node: T | null) => {
      const previous = listRef.current;
      listRef.current = node;

      if (previous && previous !== node) {
        resizeObserverRef.current?.unobserve(previous);
      }
      if (node) {
        resizeObserverRef.current?.observe(node);
      }

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }

      measureRef.current();
    },
    [forwardedRef],
  );

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const entries = Array.from(tabsRef.current.entries());
    const activeEntry = entries.find(([, entry]) => entry.active);
    const pillTargetId = hoveredTabIdRef.current ?? activeEntry?.[0] ?? null;
    const pillTarget = pillTargetId
      ? tabsRef.current.get(pillTargetId)
      : undefined;
    const listRect = list.getBoundingClientRect();

    if (!pillTarget) {
      setPill((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    } else {
      const box = tabBox(pillTarget.node, list, listRect);
      const next = { ...box, visible: box.width > 0 };
      setPill((prev) => (sameRect(prev, next) ? prev : next));
    }

    if (!activeEntry) {
      setUnderline((prev) =>
        prev.visible ? { ...prev, visible: false } : prev,
      );
      return;
    }

    const box = tabBox(activeEntry[1].node, list, listRect);
    const next = {
      // Inset like inset-x-2; vertically it sits on the list bottom (divider).
      left: box.left + TAB_STRIP_UNDERLINE_INSET,
      width: Math.max(0, box.width - TAB_STRIP_UNDERLINE_INSET * 2),
      visible: box.width > 0,
    };
    setUnderline((prev) => (sameRect(prev, next) ? prev : next));
  }, []);

  measureRef.current = measure;

  const registerTab = useCallback(
    (id: string, node: HTMLElement | null, active: boolean) => {
      const previous = tabsRef.current.get(id);

      if (!node) {
        if (!previous) return;
        resizeObserverRef.current?.unobserve(previous.node);
        tabsRef.current.delete(id);
        measure();
        return;
      }

      if (previous?.node === node && previous.active === active) return;

      if (previous && previous.node !== node) {
        resizeObserverRef.current?.unobserve(previous.node);
      }
      if (!previous || previous.node !== node) {
        resizeObserverRef.current?.observe(node);
      }

      tabsRef.current.set(id, { node, active });
      measure();
    },
    [measure],
  );

  const setHoveredTabId = useCallback(
    (id: string | null) => {
      if (hoveredTabIdRef.current === id) return;
      hoveredTabIdRef.current = id;
      measure();
    },
    [measure],
  );

  useEffect(() => {
    // Absent in jsdom unless a test stubs it; measure on scroll/resize only.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            measureRef.current();
          });
    resizeObserverRef.current = observer;

    const list = listRef.current;
    if (list) observer?.observe(list);
    tabsRef.current.forEach((entry) => observer?.observe(entry.node));

    const onScroll = () => measureRef.current();
    list?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      observer?.disconnect();
      resizeObserverRef.current = null;
      list?.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return { setListRef, registerTab, setHoveredTabId, pill, underline };
}

/**
 * Registers one tab with the surrounding indicator list. `sliding` is false
 * when the tab renders outside a list (it then paints its own fill).
 */
export function useTabStripRegistration(active: boolean): {
  sliding: boolean;
  setRef: (node: HTMLElement | null) => void;
  onMouseEnter: (() => void) | undefined;
  clearHover: () => void;
} {
  const ctx = useContext(TabStripContext);
  const id = useId();
  const nodeRef = useRef<HTMLElement | null>(null);
  const activeRef = useRef(active);
  const ctxRef = useRef(ctx);

  activeRef.current = active;
  ctxRef.current = ctx;

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      ctxRef.current?.registerTab(id, node, activeRef.current);
    },
    [id],
  );

  useLayoutEffect(() => {
    if (!nodeRef.current) return;
    ctxRef.current?.registerTab(id, nodeRef.current, active);
  }, [active, id]);

  useEffect(
    () => () => {
      ctxRef.current?.registerTab(id, null, false);
    },
    [id],
  );

  const onMouseEnter = useCallback(() => {
    ctxRef.current?.setHoveredTabId(id);
  }, [id]);

  const clearHover = useCallback(() => {
    ctxRef.current?.setHoveredTabId(null);
  }, []);

  return {
    sliding: ctx != null,
    setRef,
    onMouseEnter: ctx ? onMouseEnter : undefined,
    clearHover,
  };
}

/** The sliding pill and the underline, drawn under the tabs. */
export const TabStripIndicators: FC<{
  pill: TabStripIndicatorRect;
  underline: TabStripIndicatorRect;
  showUnderline: boolean;
  heightClass: string;
  radiusClass: string;
}> = ({ pill, underline, showUnderline, heightClass, radiusClass }) => (
  <>
    <div
      aria-hidden
      data-tab-indicator="pill"
      className={cn(
        // left-0: without it the indicators sit at the static position, after the
        // list's padding, and land that many px right of the tab.
        "pointer-events-none absolute top-1/2 left-0 z-0 bg-bg-tertiary",
        heightClass,
        radiusClass,
        "transition-[transform,width,opacity] duration-200 ease-in-out",
        "motion-reduce:transition-none",
        pill.visible ? "opacity-100" : "opacity-0",
      )}
      style={{
        width: pill.width,
        transform: `translateX(${pill.left}px) translateY(-50%)`,
      }}
    />
    {showUnderline && (
      <div
        aria-hidden
        data-tab-indicator="underline"
        className={cn(
          "pointer-events-none absolute bottom-0 left-0 z-0 h-px rounded-full bg-text-primary",
          "transition-[transform,width,opacity] duration-200 ease-in-out",
          "motion-reduce:transition-none",
          underline.visible ? "opacity-100" : "opacity-0",
        )}
        style={{
          width: underline.width,
          transform: `translateX(${underline.left}px)`,
        }}
      />
    )}
  </>
);

const SCROLL_BUTTON_CLASSNAME =
  "absolute flex size-6 items-center justify-center rounded-md bg-bg-primary/90 text-icon-secondary ring-1 ring-border-primary hover:bg-bg-secondary hover:text-icon-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border";

/**
 * Scroll state for a horizontally scrolling tab list: edge flags, vertical
 * wheel → horizontal scroll, and paging. `watch` re-observes the list's
 * children when it changes; pass null when the list doesn't scroll.
 */
export type TabStripScrollState<T extends HTMLElement> = {
  scrollRef: MutableRefObject<T | null>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollByPage: (direction: "left" | "right") => void;
};

export function useTabStripScroll<T extends HTMLElement>(
  watch: unknown,
): TabStripScrollState<T> {
  const scrollRef = useRef<T | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  }, []);

  const scrollByPage = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8,
      behavior: "smooth",
    });
  }, []);

  const enabled = watch !== null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    // Native and non-passive: React's onWheel is passive, so its
    // preventDefault can't stop the page scrolling along with the tabs.
    const onWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      el.removeEventListener("wheel", onWheel);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [watch, enabled, updateScrollState]);

  return { scrollRef, canScrollLeft, canScrollRight, scrollByPage };
}

/**
 * The frame around a scrolling tab list: edge fades from bg-bg-primary and
 * size-6 page buttons that appear once the list overflows that side.
 */
export const TabStripScrollFrame: FC<{
  className: string;
  style?: CSSProperties;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollByPage: (direction: "left" | "right") => void;
  children: ReactNode;
}> = ({
  className,
  style,
  canScrollLeft,
  canScrollRight,
  scrollByPage,
  children,
}) => (
  <div className={className} style={style}>
    {children}
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-bg-primary to-transparent transition-opacity duration-150",
        canScrollLeft ? "opacity-100" : "opacity-0",
      )}
    />
    {canScrollLeft ? (
      <button
        type="button"
        aria-label="Scroll tabs left"
        onClick={() => scrollByPage("left")}
        className={cn("left-0", SCROLL_BUTTON_CLASSNAME)}
      >
        <ChevronLeftIcon />
      </button>
    ) : null}
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-bg-primary to-transparent transition-opacity duration-150",
        canScrollRight ? "opacity-100" : "opacity-0",
      )}
    />
    {canScrollRight ? (
      <button
        type="button"
        aria-label="Scroll tabs right"
        onClick={() => scrollByPage("right")}
        className={cn("right-0", SCROLL_BUTTON_CLASSNAME)}
      >
        <ChevronRightIcon />
      </button>
    ) : null}
  </div>
);

/** The count chip after a tab label. */
export const TabStripCount: FC<{ count: number }> = ({ count }) => (
  <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-md bg-state-pressed px-1 font-sans-v4 text-2xs-v4 leading-4 font-medium-v4 text-text-secondary">
    {count}
  </span>
);
